import {makeAutoObservable, observable, runInAction} from 'mobx';

import {User, UserSharedDTO} from '@/domain';
import {LegalDocumentBits} from '@/domain/dtos/Home';
import {type Constants, defaultConstants} from '@/domain/models/Constants';
import {isArraySimpleEqual} from '@/domain/models/Model';
import {ONE_SECOND_MS} from '@/shared/consts/time';
import {Timer} from '@/shared/types/timer';

import {autoupdateCurrentTime} from './autoupdateCurrentTime';
import {SharedProfileError} from './errors';
import {
  AVAILABLE_AB_TESTS,
  FiltersModalView,
  ProfileModal,
  UserModal,
} from './types';

type NewBump = {
  user: User;
  matchId: number;
  suggestions: Array<string>;
};

const MIN_SNOW_LOADING_MODAL = ONE_SECOND_MS;

class AppStore {
  sharedProfile: UserSharedDTO | SharedProfileError | null = null;
  activeFilterModalView: FiltersModalView | undefined;
  private _currentTime = Date.now();
  private activeProfileModals = new Set<ProfileModal>();
  private _promptIndex: number | undefined;
  private enabledSignUpTests = observable.set<string>();
  private _singUpTestsReady = false;
  private enabledUserTests = observable.set<string>();
  private _usersModals: UserModal[] = observable.array([], {deep: false});
  private _constants: Constants = defaultConstants;
  private _updatedLegalDocuments: LegalDocumentBits = LegalDocumentBits.none;
  private _newBump: NewBump | null = null;
  private _showingAppLoadingModal:
    | {
        startedAt: Date;
        closeTimer?: Timer;
      }
    | undefined;

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentTime(currentTime: number) {
    this._currentTime = currentTime;
  }

  get currentTime() {
    return this._currentTime;
  }

  get newBump() {
    return this._newBump;
  }

  get showingAppLoadingModal(): boolean {
    return this._showingAppLoadingModal !== undefined;
  }

  openProfileModal(modal: ProfileModal) {
    this.activeProfileModals.add(modal);
  }

  closeProfileModal(modal: ProfileModal) {
    this.activeProfileModals.delete(modal);
  }

  isProfileModalOpen(modal: ProfileModal) {
    return this.activeProfileModals.has(modal);
  }

  get hasActiveProfileModal() {
    return this.activeProfileModals.size > 0;
  }

  get activePromptIndex(): number | undefined {
    return this._promptIndex;
  }

  openPromptModal(index: number) {
    this._promptIndex = index;
    this.openProfileModal(ProfileModal.Prompts);
  }

  closePromptModal() {
    this._promptIndex = undefined;
    this.closeProfileModal(ProfileModal.Prompts);
  }

  setSingUpTestsReady(readySingUp: boolean) {
    this._singUpTestsReady = readySingUp;
  }

  get singUpTestsReady() {
    return this._singUpTestsReady;
  }

  addSignUpAbTests(names: string[]) {
    for (const name of names) {
      this.enabledSignUpTests.add(name);
    }
  }

  addUserAbTests(names: string[]) {
    for (const name of names) {
      this.enabledUserTests.add(name);
    }
  }

  replaceUserAbTests(names: string[]) {
    if (isArraySimpleEqual(names, [...this.enabledUserTests])) {
      return;
    }

    this.enabledUserTests.replace(names);
  }

  activeAbTest(name: AVAILABLE_AB_TESTS): boolean {
    return this.enabledUserTests.has(name) || this.enabledSignUpTests.has(name);
  }

  clearAbTests() {
    this.enabledSignUpTests.clear();
    this.enabledUserTests.clear();
  }

  openFilterModal(name: FiltersModalView) {
    this.activeFilterModalView = name;
  }

  closeFilterModal() {
    this.activeFilterModalView = undefined;
  }

  get usersModals() {
    return this._usersModals;
  }

  addUserModal(modalUser: UserModal) {
    if (this._usersModals.some((user) => user.user.id === modalUser.user.id)) {
      return;
    }

    this._usersModals.push(modalUser);
  }

  removeUserModal(userId: UserModal['user']['id']) {
    this._usersModals = this._usersModals.filter(
      (user) => user.user.id !== userId
    );
  }

  get constants() {
    return this._constants;
  }

  setConstants(constants: Constants) {
    this._constants = constants;
  }

  get updatedLegalDocuments() {
    return this._updatedLegalDocuments;
  }

  get hasUpdatedLegalDocuments() {
    return this._updatedLegalDocuments !== LegalDocumentBits.none;
  }

  setUpdatedLegalDocuments(documents: LegalDocumentBits) {
    this._updatedLegalDocuments = documents;
  }

  clearUpdatedLegalDocuments() {
    this._updatedLegalDocuments = LegalDocumentBits.none;
  }

  get enabledDiscoveryPassAndSmashSwipes() {
    return !this._constants.no_swipes;
  }

  setNewBump(bump: NewBump) {
    this._newBump = bump;
  }

  clearNewBump() {
    this._newBump = null;
  }

  showAppLoadingModal() {
    if (this._showingAppLoadingModal?.closeTimer) {
      clearTimeout(this._showingAppLoadingModal.closeTimer);
    }

    this._showingAppLoadingModal = {
      startedAt: new Date(),
    };
  }

  hideAppLoadingModal() {
    // already schedule to close
    if (this._showingAppLoadingModal?.closeTimer) {
      return;
    }

    if (!this._showingAppLoadingModal?.startedAt) {
      this._showingAppLoadingModal = undefined;
      return;
    }

    const keepTimeout =
      MIN_SNOW_LOADING_MODAL -
      (Date.now() - Number(this._showingAppLoadingModal.startedAt));

    if (keepTimeout > 0) {
      setTimeout(() => {
        runInAction(() => {
          this._showingAppLoadingModal = undefined;
        });
      }, keepTimeout);
      return;
    }

    this._showingAppLoadingModal = undefined;
  }
}

const store = new AppStore();

autoupdateCurrentTime(() => {
  store.setCurrentTime(Date.now());
}, 10 * ONE_SECOND_MS);

export default store;
